/** @jsxImportSource theme-ui */
import React from 'react';
import { Box, Text, Card } from '@infotrack/zenith-ui';
import type { ITopFiveApiV2EndpointsAuthMeResponse } from '@/api/models';
import { userInfoSectionStyles } from './userInfoSection.styles';

export const UserInfoSection: React.FC<{
  apiUser: ITopFiveApiV2EndpointsAuthMeResponse;
}> = ({ apiUser }) => {
  if (!apiUser) return null;

  return (
    <Card
      padding={['large']}
      borderRadius="medium"
      borderColor="b-300"
      boxShadow="medium"
    >
      <Box sx={{ mb: 3 }}>
        <Text variant="title" as="h3">
          User Information from API
        </Text>
      </Box>
      <Box sx={userInfoSectionStyles.contentGrid}>
        {apiUser.authenticated !== undefined && (
          <InfoField
            label="Status"
            value={
              apiUser.authenticated ? 'Authenticated' : 'Not Authenticated'
            }
            variant={apiUser.authenticated ? 'success' : 'error'}
          />
        )}
        {apiUser.firstName && (
          <InfoField label="First Name" value={apiUser.firstName} />
        )}
        {apiUser.lastName && (
          <InfoField label="Last Name" value={apiUser.lastName} />
        )}
        {apiUser.preferredName && (
          <InfoField label="Preferred Name" value={apiUser.preferredName} />
        )}
        {apiUser.email && (
          <Box sx={userInfoSectionStyles.fullWidthGridItem}>
            <InfoField label="Email Address" value={apiUser.email} />
          </Box>
        )}
        {apiUser.permissions && (
          <Box sx={userInfoSectionStyles.claimsContainer}>
            <Box sx={{ mb: 2 }}>
              <Text variant="subtitle">Permissions</Text>
            </Box>
            <Box sx={userInfoSectionStyles.claimsContent}>
              {Object.entries(apiUser.permissions).map(([key, value]) => (
                <Box key={key} sx={userInfoSectionStyles.claimItem}>
                  <Box sx={userInfoSectionStyles.claimKey}>{key}:</Box>{' '}
                  <Text
                    variant="medium"
                    color={value ? 'success' : 'error'}
                  >
                    {value ? 'Yes' : 'No'}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {apiUser.claims && Object.keys(apiUser.claims).length > 0 && (
          <Box sx={userInfoSectionStyles.claimsContainer}>
            <Box sx={{ mb: 2 }}>
              <Text variant="subtitle">Claims</Text>
            </Box>
            <Box sx={userInfoSectionStyles.claimsContent}>
              {Object.entries(apiUser.claims).map(([key, values]) => (
                <Box key={key} sx={userInfoSectionStyles.claimItem}>
                  <Box sx={userInfoSectionStyles.claimKey}>{key}:</Box>{' '}
                  <Text variant="medium">
                    {Array.isArray(values) ? values.join(', ') : values}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
};

interface InfoFieldProps {
  label: string;
  value: string;
  variant?: 'success' | 'error' | 'default';
}

const InfoField: React.FC<InfoFieldProps> = ({
  label,
  value,
  variant = 'default',
}) => (
  <Box sx={userInfoSectionStyles.infoField}>
    <Box sx={userInfoSectionStyles.infoFieldLabel}>{label}</Box>
    <Text
      variant="medium"
      color={
        variant === 'success'
          ? 'success'
          : variant === 'error'
            ? 'error'
            : undefined
      }
    >
      {value}
    </Text>
  </Box>
);
